import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import DevotionCalendarCell from "./DevotionCalendarCell";
import { useDevotionCalendar } from "../../../hooks/useDevotionCalendar";
import { parseDateOnly, shiftMonth } from "../../../utils/dateOnly";

const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";
const NEU_RAISED_MD =
  "shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DevotionCalendarPopover = ({
  open,
  onClose,
  onSelect,
  anchorRef,
  date,
  language = "en",
  className = "absolute left-0 top-full z-40 mt-2 w-[min(92vw,340px)]",
}) => {
  const popoverRef = useRef(null);
  const [month, setMonth] = useState(() => {
    const parsed = parseDateOnly(date);
    return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 };
  });
  const { loadMonth, getState } = useDevotionCalendar(language);
  const state = getState(month.year, month.month);

  useEffect(() => {
    if (open) loadMonth(month.year, month.month);
  }, [loadMonth, month, open]);

  useEffect(() => {
    const outside = (event) => {
      if (
        open &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !anchorRef?.current?.contains(event.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    const onEsc = (event) => {
      if (event.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const days = state.data?.days || [];
  const todayISO = state.data?.today;
  const firstWeekday = new Date(month.year, month.month - 1, 1).getDay();
  const cells = [...Array(firstWeekday).fill(null), ...days];
  while (cells.length % 7) cells.push(null);

  const monthLabel = new Date(month.year, month.month - 1).toLocaleDateString(
    language === "ko" ? "ko-KR" : "en-US",
    { month: "long", year: "numeric" }
  );

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Choose devotion date"
      className={`rounded-[1.25rem] bg-canvas-base p-3 ${NEU_RAISED_MD} ${className}`}
    >
      {/* ---- Month navigation ---- */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <button
          type="button"
          onClick={() => setMonth((v) => shiftMonth(v.year, v.month, -1))}
          className={`w-9 h-9 rounded-full flex items-center justify-center bg-canvas-base ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all text-neu-primary`}
          aria-label="Previous month"
        >
          <FiChevronLeft aria-hidden="true" />
        </button>
        <h2 className="font-noto-serif text-headline-sm text-neu-primary text-center flex-1">
          {monthLabel}
        </h2>
        <button
          type="button"
          onClick={() => setMonth((v) => shiftMonth(v.year, v.month, 1))}
          className={`w-9 h-9 rounded-full flex items-center justify-center bg-canvas-base ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all text-neu-primary`}
          aria-label="Next month"
        >
          <FiChevronRight aria-hidden="true" />
        </button>
      </div>

      {/* ---- Weekday header ---- */}
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wider mb-1">
        {WEEKDAYS.map((wd, i) => (
          <span
            key={wd}
            className={
              i === 0
                ? "text-[#b02a37]/80"
                : i === 6
                  ? "text-[#2a6fb0]/80"
                  : "text-slate-muted"
            }
          >
            {wd}
          </span>
        ))}
      </div>

      {/* ---- Loading / error ---- */}
      {state.loading && (
        <p className="py-2 text-label-sm text-slate-muted text-center" aria-live="polite">
          Loading…
        </p>
      )}
      {state.error && (
        <p className="py-2 text-label-sm text-[#b02a37] text-center" aria-live="polite">
          {state.error}
        </p>
      )}

      {/* ---- Day grid ---- */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) =>
          day ? (
            <DevotionCalendarCell
              key={day.date}
              day={day}
              compact
              today={day.date === todayISO}
              selected={day.date === date}
              onSelect={onSelect}
            />
          ) : (
            <div key={`e-${i}`} className="min-h-[44px]" aria-hidden="true" />
          )
        )}
      </div>

      {/* ---- Legend ---- */}
      <ul className="flex items-center justify-center gap-3 pt-2 mt-1 text-[11px] text-slate-muted">
        <li className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]" aria-hidden="true" />
          Reflection
        </li>
        <li className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-cta" aria-hidden="true" />
          Sunday
        </li>
        <li className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded border border-accent-cta" aria-hidden="true" />
          Today
        </li>
      </ul>
    </div>
  );
};

export default DevotionCalendarPopover;
